import { useAppSelector } from '~/store/hooks'
import routes from 'config/routes'
import { useCFDStatusFragment } from 'hooks/useCFDStatus'
import { Edge, isPointInPolygon } from '~/utils/pointInPolygon'
import { Button } from '~/ui/Button'
import { useNavigate, useLocation } from 'react-router-dom'
import { Icon } from '~/ui/Icon'
import { selectDenormalizedWalls } from '~/store/objects/selectors/walls'
import { wallIsValidAndPolygon } from '~/store/objects/walls'

const useIsProductWithinBounds = () => {
  const products = useAppSelector(({ objects }) => Object.values(objects.present.products))
  const exteriorWalls = useAppSelector(selectDenormalizedWalls).filter(({ layerKey }) => layerKey === 'EXTERIOR_WALLS')
  const wallEdges = exteriorWalls.map<Edge[]>(({ segments }) =>
    segments.map(({ insetEdge }) => ({
      p1: [insetEdge.start.x, insetEdge.start.y],
      p2: [insetEdge.end.x, insetEdge.end.y],
    }))
  )
  return products.every(({ position: { x, y } }) => wallEdges.some(edges => isPointInPolygon([x, y], edges)))
}

const useIsEnclosedSpace = () => {
  const segments = useAppSelector(state => state.objects.present.segments)
  const walls = useAppSelector(state => state.objects.present.objects)
  return Object.values(walls)
    .filter(({ layerKey }) => layerKey === 'EXTERIOR_WALLS')
    .some(wall => wallIsValidAndPolygon(segments, wall))
}

export const ConfigureCFDButton = () => {
  const { cfdStatus, complete } = useCFDStatusFragment()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const products = useAppSelector(({ objects }) => Object.values(objects.present.products))
  const isProductsWithinBounds = useIsProductWithinBounds()
  const isEnclosedSpace = useIsEnclosedSpace()

  const errorMessage = cfdStatus?.isSomeInProgress
    ? 'Please cancel existing simulation before running a new one.'
    : !isEnclosedSpace
      ? "Please complete the facility's exterior walls."
      : products.length === 0
        ? 'Please add products to the facility.'
        : !isProductsWithinBounds
          ? 'A fan is out of bounds. Please delete or move the fan to a valid location.'
          : undefined

  return (
    <>
      <Button
        onPress={() => navigate(`${pathname.replace(/\/+$/, '')}${routes.modals.configureCFD}`)}
        isDisabled={Boolean(errorMessage) || !complete}
      >
        {!complete ? 'Loading...' : 'Configure CFD'}
      </Button>
      {errorMessage ? (
        <div className="flex gap-2 items-center outline outline-red-900 p-2 rounded bg-red-100">
          <Icon name="warn" size="16" className="text-red-900" />
          <p className="text-red-900">{errorMessage}</p>
        </div>
      ) : null}
    </>
  )
}
